/**
 * Thin HTTP wrapper for the non-streaming chat-api endpoints.
 * Streaming (/chat) lives in sse.ts because it needs reader semantics.
 */

import type { AgentConfigResponse, SessionResponse } from '@showpilot/core';

/**
 * Trigger record returned by /v1/discover for a matched path. The widget
 * passes these to `installTriggerWatchers` (`./trigger-watcher.ts`). Old
 * server bundles that don't yet include `triggers` have it as `[]`.
 */
export type DiscoveredTrigger = {
  /** The deployment (placement) that authored this trigger — echoed on
   * presence events (D1, agent-deployments strategy). Optional: older
   * chat-api builds don't emit it. */
  deploymentId?: string;
  type: 'page_load' | 'time_on_page' | 'scroll_depth' | 'exit_intent' | 'utm' | 'element';
  meta: {
    duration?: number;
    percentage?: number;
    property?: string;
    value?: string;
    elementSelector?: string;
    elementTriggerType?: 'exists' | 'visible' | 'click';
    elementWidgetType?: 'launcher' | 'embed';
    /** Presence anchor — maps to data-showpilot-position on the mount. */
    position?: 'right' | 'center';
  };
  agentPublicId: string;
  view: string;
  // Presence model (mobile UX program, Phase 0) — server derives these
  // read-time from `view` (launcher ← chat-button · bar ← site-agent ·
  // card ← engage · launcher+autoOpen ← rail). Optional: older chat-api
  // builds don't emit them; the widget falls back to `view`.
  presence?: 'launcher' | 'bar' | 'card';
  autoOpen?: 'desktop-tablet';
  // Arrival destination (campaign triggers land INSIDE the experience
  // viewer). Typed now; authored + dispatched in later phases.
  destination?: 'experience';
  experienceId?: string;
};

/**
 * Offers v2 — a LIVE offer deploy compiled by /v1/discover for this
 * page. Deploys live ON THE OFFER (offers.deploy), independent of the
 * agent's targeting: pages + the date window matched server-side; the
 * `conditions` here are the client-evaluated remainder (the server
 * never sees the query string).
 */
export type ArrivalOffer = {
  presentation: 'interstitial' | 'toast' | 'banner';
  conditions?: {
    utm?: { property: string; value: string };
    /** Referrer hosts — suffix match on document.referrer's host. */
    referrerHosts?: string[];
    /** New vs returning visitor (first-seen marker). */
    visitorType?: 'new' | 'returning';
    /** Device classes to present on (phone→'mobile', tablet→'desktop'). */
    devices?: Array<'desktop' | 'mobile'>;
    afterSeconds?: number;
  };
  offer: DiscoveredOffer;
  design: OfferDesignData | null;
};

/** Serialized offer payload riding an ArrivalOffer. */
export type DiscoveredOffer = {
  id: string;
  imageUrl: string | null;
  headline: string | null;
  message: string;
  ctaLabel: string | null;
  ctaAction:
    | { type: 'none' }
    | { type: 'book_meeting' }
    | { type: 'capture_lead' }
    | { type: 'navigate'; url: string }
    | { type: 'open_content'; contentRefId: string }
    | { type: 'open_experience'; experienceId: string };
  captureEmail: boolean;
};

/**
 * Offers v2 — the creative as data (mirrors OfferDesign in the db
 * package; defined locally because the widget bundle must not import
 * server DB types). Rendered by the offer surfaces.
 */
export type OfferDesignData = {
  layout?: 'card' | 'full-bleed' | 'split' | 'event';
  /** Card width scale (absent = md). */
  size?: 'sm' | 'md' | 'lg';
  eyebrow?: string;
  background?: { color?: string; imageUrl?: string; scrim?: number };
  /** Soft tint behind the split layout's copy panel. */
  bodyTint?: string;
  text?: { color?: string; headlineScale?: 'md' | 'lg' | 'xl'; fontFamily?: string };
  button?: { background?: string; color?: string; radiusPx?: number };
  declineLabel?: string;
  entrance?: 'slide-up' | 'fade';
};

/** Result of calling /v1/discover. Null = no agent for this host. */
export type DiscoverResult =
  | {
      kind: 'matched';
      agentPublicId: string;
      view: string;
      /** See DiscoveredTrigger.presence — optional on older servers. */
      presence?: 'launcher' | 'bar' | 'card';
      autoOpen?: 'desktop-tablet';
      triggers: DiscoveredTrigger[];
      /** Offers v2 — live offer deploys for this page (may be absent). */
      arrivalOffers?: ArrivalOffer[];
      /**
       * Offers v3 — identified-visitor hint, present only when arrival
       * offers rode along AND the fingerprint resolved to a visitor with
       * a captured email. Surfaces skip the email field (one-tap accept)
       * and may greet by first name. Never carries the email itself.
       */
      offerIdentity?: { identified: true; firstName?: string };
    }
  | { kind: 'none' }
  | { kind: 'ambiguous' }
  | { kind: 'error'; status: number };

export async function fetchConfig(
  apiBaseUrl: string,
  publicId: string,
  signal?: AbortSignal,
  agentPreviewToken?: string | null,
): Promise<AgentConfigResponse> {
  // Draft preview (publish model): the signed token makes the server
  // return the operator's draft instead of the published revision.
  const preview = agentPreviewToken ? `?preview=${encodeURIComponent(agentPreviewToken)}` : '';
  const res = await fetch(`${apiBaseUrl}/v1/agents/${publicId}/config${preview}`, {
    method: 'GET',
    headers: { accept: 'application/json' },
    signal,
  });
  if (!res.ok) {
    // Plan refusals (trial ended, feature off) carry a code the boot path
    // uses to render nothing; every other failure keeps the defaults path.
    const body = (await res.json().catch(() => null)) as { error?: { code?: string } } | null;
    throw Object.assign(new Error(`config ${res.status}`), {
      status: res.status,
      code: body?.error?.code,
    });
  }
  // Trusted first-party API — structural spot-check instead of a zod
  // parse (zod was ~13 KB gzip of the widget budget, and config.ts
  // re-normalizes every field defensively anyway).
  const json = (await res.json()) as AgentConfigResponse;
  if (!json || typeof json !== 'object' || !json.agent) {
    throw new Error('config: malformed response');
  }
  return json;
}

/**
 * Universal-embed agent discovery (Slice B1/B2). Caller passes the
 * current page's host + path. Server matches against
 * `targeting_rules.origins` and returns an agent + layout when there's
 * exactly one match. 404 means "no agent for this host — render
 * nothing"; 409 means multiple orgs claim the host.
 */
const PRESENCES = new Set(['launcher', 'bar', 'card']);
/**
 * Dev-only view / presence override, read from the page's query string:
 * `?sp_view=site-agent&sp_presence=bar`. Explicit params only — absent
 * params force nothing, and an unknown presence is ignored.
 */
export function forcedPresence(
  search: string = typeof location !== 'undefined' ? location.search : '',
): { view?: string; presence?: 'launcher' | 'bar' | 'card' } {
  const q = new URLSearchParams(search);
  const view = q.get('sp_view')?.trim() || undefined;
  const p = q.get('sp_presence')?.trim();
  const presence = p && PRESENCES.has(p) ? (p as 'launcher' | 'bar' | 'card') : undefined;
  return { ...(view ? { view } : {}), ...(presence ? { presence } : {}) };
}

export async function discoverAgentForHost(
  apiBaseUrl: string,
  host: string,
  path: string,
  signal?: AbortSignal,
  previewToken?: string,
  fingerprint?: string,
  agentPreviewToken?: string | null,
): Promise<DiscoverResult> {
  const url =
    `${apiBaseUrl}/v1/discover?host=${encodeURIComponent(host)}` +
    `&path=${encodeURIComponent(path)}` +
    (previewToken ? `&preview=${encodeURIComponent(previewToken)}` : '') +
    // Draft preview (publish model) — force-mounts the previewed agent
    // on any of the org's sites; server verifies signature + org.
    (agentPreviewToken ? `&agent_preview=${encodeURIComponent(agentPreviewToken)}` : '') +
    // CRM-conditioned offers resolve the visitor server-side.
    (fingerprint ? `&fp=${encodeURIComponent(fingerprint)}` : '');
  let res: Response;
  try {
    res = await fetch(url, { method: 'GET', headers: { accept: 'application/json' }, signal });
  } catch {
    return { kind: 'error', status: 0 };
  }
  if (res.status === 200) {
    const body = (await res.json()) as {
      agentPublicId?: string;
      view?: string;
      presence?: 'launcher' | 'bar' | 'card';
      autoOpen?: 'desktop-tablet';
      triggers?: DiscoveredTrigger[];
      arrivalOffers?: ArrivalOffer[];
    };
    if (body.agentPublicId) {
      // Dev-only: `?sp_view=site-agent&sp_presence=bar` previews a view +
      // presence this host's deployment does not place, without touching
      // the deployment (David 2026-10-25: "I can't trigger the site agent
      // bar on desktop" — neither test site deploys it). Explicit params
      // only, inert otherwise; the view's own serving gate still applies.
      const forced = forcedPresence();
      const view = forced.view ?? body.view ?? 'site-agent';
      const presence = forced.presence ?? body.presence;
      const triggers = (Array.isArray(body.triggers) ? body.triggers : []).map((t) =>
        forced.view || forced.presence ? { ...t, view, ...(presence ? { presence } : {}) } : t,
      );
      return {
        kind: 'matched',
        agentPublicId: body.agentPublicId,
        view,
        presence,
        autoOpen: body.autoOpen,
        triggers,
        ...(Array.isArray(body.arrivalOffers) && body.arrivalOffers.length > 0
          ? { arrivalOffers: body.arrivalOffers }
          : {}),
      };
    }
    return { kind: 'error', status: 200 };
  }
  if (res.status === 404) return { kind: 'none' };
  if (res.status === 409) return { kind: 'ambiguous' };
  return { kind: 'error', status: res.status };
}

/**
 * Server response when posting a journey answer. `next` is null when
 * the journey is complete; otherwise it's the structured next step.
 */
export type JourneyAnswerResponse = {
  ok: true;
  completed: boolean;
  answeredCount?: number;
  next?: {
    step: {
      id: string;
      title: string;
      type: 'message' | 'open_ended' | 'single_select' | 'multi_select' | 'yes_no' | 'action';
      prompt: string;
      isRequired?: boolean;
      responseType?: string;
      options?: Array<{ id: string; label: string; value: string }>;
      settings?: Record<string, unknown>;
    };
    index: number;
    total: number;
  };
};

/**
 * POST a journey answer (option click) to the chat-api. Used for
 * single_select / multi_select / yes_no — open_ended answers come
 * through the regular chat input. The response either advances the
 * journey or signals completion.
 */
export async function submitJourneyAnswer(
  apiBaseUrl: string,
  publicId: string,
  data: { sessionId: string; stepId: string; value: string | string[] | null },
  signal?: AbortSignal,
): Promise<JourneyAnswerResponse> {
  const res = await fetch(`${apiBaseUrl}/v1/agents/${publicId}/journey-answer`, {
    method: 'POST',
    headers: { 'content-type': 'application/json', accept: 'application/json' },
    body: JSON.stringify(data),
    signal,
  });
  if (!res.ok) throw new Error(`journey-answer ${res.status}`);
  return (await res.json()) as JourneyAnswerResponse;
}

/** Content the /unlock endpoint releases for a gated experience. */
export interface ExperienceUnlockResponse {
  gate: 'none' | 'capture' | 'reverse';
  playlist: Array<{
    content_id: string;
    content_type: string;
    content_extension: string;
    name: string;
    image?: string | null;
    url?: string | null;
  }>;
  totalCount: number;
  previewCount: number;
  fields: string[];
  /** G-1 — the content_gate form's full field list (parsed by the gate
   *  card via parseFormFields; unknown here since it's server JSON). */
  formFields?: unknown;
  /** G-1 — capture submits echo this so the after-ladder runs. */
  formId?: string | null;
}

/**
 * Resolve the gate for a shared experience (P2). The widget sends its
 * fingerprint; the server checks the PASSPORT (already captured for the
 * org) and returns the content the visitor may see. Called on boot for
 * gated/reverse experiences and again after a capture submit.
 */
export async function unlockExperience(
  apiBaseUrl: string,
  experiencePublicId: string,
  fingerprint: string,
): Promise<ExperienceUnlockResponse> {
  const res = await fetch(`${apiBaseUrl}/v1/experiences/${experiencePublicId}/unlock`, {
    method: 'POST',
    headers: { 'content-type': 'application/json', accept: 'application/json' },
    body: JSON.stringify({ fingerprint }),
  });
  if (!res.ok) throw new Error(`experience-unlock ${res.status}`);
  return (await res.json()) as ExperienceUnlockResponse;
}

/**
 * Generate (or fetch from cache) TTS audio for `text` in the agent's
 * voice. Used by the Talk Now greeter button — plays the agent's
 * `welcomeMessage` on click.
 */
export async function speakText(
  apiBaseUrl: string,
  publicId: string,
  text: string,
  signal?: AbortSignal,
): Promise<{ audioUrl: string }> {
  const res = await fetch(`${apiBaseUrl}/v1/agents/${publicId}/speak`, {
    method: 'POST',
    headers: { 'content-type': 'application/json', accept: 'application/json' },
    body: JSON.stringify({ text }),
    signal,
  });
  if (!res.ok) throw new Error(`speak ${res.status}`);
  return (await res.json()) as { audioUrl: string };
}

/**
 * Fire-and-forget event POST — used to log content opens (and other
 * widget-side actions) that bypass the LLM so the admin Conversations
 * inbox + Content tab + Visitor 360 can show the full picture.
 *
 * Never throws — best-effort. A dropped event is preferable to a
 * thrown promise that interrupts the visitor flow.
 */
export function postSessionEvent(
  apiBaseUrl: string,
  sessionId: string,
  body: {
    type:
      | 'content-opened'
      | 'content-progress'
      | 'content-completed'
      | 'agent-link-clicked'
      | 'recommendations-rendered'
      | 'meeting-booked'
      // Slot-offer ladder telemetry (rep/booking slice 1) — the rungs
      // only the widget observes: chip taps + client-side modal falls.
      | 'meeting-slot-tapped'
      | 'meeting-fell-to-modal'
      // Email-first booking step (prequalify P-1) — shown/given rungs.
      | 'meeting-email-shown'
      | 'meeting-email-given'
      // Presenter STAGE TRUTH — where the tour on the stage actually is
      // after every applied move (screen-truth loop, slice 1).
      | 'presenter-screen'
      // Presenter rails — a bot voice turn finished playing out
      // (WebAudio silence detection); logged while a tour is on stage.
      | 'presenter-playout'
      // Where the seconds went between the pill and the face (connect-timing.ts).
      | 'voice-connect-timing';
    payload: Record<string, unknown>;
  },
): void {
  try {
    void fetch(`${apiBaseUrl}/v1/sessions/${sessionId}/events`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        accept: 'application/json',
      },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => {});
  } catch {
    // Swallow — never break the visitor flow on tracking.
  }
}

/**
 * Prequalify P-2 — post-email route-readiness. Asks the server: can
 * routing place this visitor confidently now? Not ready → the gap
 * fields the org's rules need. Ready → possibly a new rep + full slot
 * set (the post-identify re-route). Null on any failure — the card
 * falls back to its pre-email routing.
 */
export interface RouteReadinessResult {
  ready: boolean;
  repUnchanged?: boolean;
  rep?: { name?: string | null; title?: string | null; avatarUrl?: string | null } | null;
  startsIso?: string[];
  durationMinutes?: number;
  nextFromIso?: string | null;
  gaps?: Array<{
    key: string;
    label: string;
    why: string;
    options?: Array<{ value: string; label: string }>;
  }>;
}

export async function postRouteReadiness(
  apiBaseUrl: string,
  sessionId: string,
  body: { answers?: Record<string, string>; force?: boolean },
): Promise<RouteReadinessResult | null> {
  try {
    const res = await fetch(`${apiBaseUrl}/v1/sessions/${sessionId}/route-readiness`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) return null;
    return (await res.json()) as RouteReadinessResult;
  } catch {
    return null;
  }
}

/**
 * Offers v3-B — book a proposed meeting slot deterministically. The
 * server re-resolves the agent's booking link and books via the
 * provider API.
 *
 * Failure is graded (rep/booking slice 1): `slot-taken` arrives with
 * fresh times so the chips re-offer inline (conflict rung);
 * `already-booked` means this session has a meeting; anything else
 * ladders down to the embed modal.
 */
export type BookSlotOutcome =
  | { ok: true; startIso: string }
  | {
      ok: false;
      code?: string;
      freshSlots?: Array<{ startIso: string }>;
      durationMinutes?: number;
    };

export async function postBookSlot(
  apiBaseUrl: string,
  sessionId: string,
  body: { startIso: string; email?: string; name?: string; timezone: string },
): Promise<BookSlotOutcome> {
  try {
    const res = await fetch(
      `${apiBaseUrl}/v1/sessions/${encodeURIComponent(sessionId)}/book-slot`,
      {
        method: 'POST',
        headers: { 'content-type': 'application/json', accept: 'application/json' },
        body: JSON.stringify(body),
      },
    );
    const json = (await res.json().catch(() => ({}))) as {
      ok?: boolean;
      startIso?: string;
      error?: { code?: string };
      freshSlots?: Array<{ startIso?: string }>;
      durationMinutes?: number;
    };
    if (res.ok && json.ok && typeof json.startIso === 'string') {
      return { ok: true, startIso: json.startIso };
    }
    const freshSlots = Array.isArray(json.freshSlots)
      ? json.freshSlots
          .filter((s): s is { startIso: string } => typeof s?.startIso === 'string')
          .slice(0, 3)
      : undefined;
    return {
      ok: false,
      ...(json.error?.code ? { code: json.error.code } : {}),
      ...(freshSlots ? { freshSlots } : {}),
      ...(typeof json.durationMinutes === 'number'
        ? { durationMinutes: json.durationMinutes }
        : {}),
    };
  } catch {
    return { ok: false };
  }
}

/**
 * Slice 3 — the booking picker's data feed: the full availability
 * window for this session's routed engine, optionally paged with
 * `fromIso` ("Later dates"). Null on any failure — the picker keeps
 * whatever it already has.
 */
export async function fetchMeetingOptions(
  apiBaseUrl: string,
  sessionId: string,
  fromIso?: string,
): Promise<{
  startsIso: string[];
  durationMinutes: number;
  needsEmail: boolean;
  nextFromIso: string | null;
} | null> {
  try {
    const qs = fromIso ? `?fromIso=${encodeURIComponent(fromIso)}` : '';
    const res = await fetch(
      `${apiBaseUrl}/v1/sessions/${encodeURIComponent(sessionId)}/meeting-options${qs}`,
      { headers: { accept: 'application/json' } },
    );
    if (!res.ok) return null;
    const json = (await res.json()) as {
      startsIso?: string[];
      durationMinutes?: number;
      needsEmail?: boolean;
      nextFromIso?: string | null;
    };
    if (!Array.isArray(json.startsIso)) return null;
    return {
      startsIso: json.startsIso.filter((s): s is string => typeof s === 'string'),
      durationMinutes: typeof json.durationMinutes === 'number' ? json.durationMinutes : 30,
      needsEmail: Boolean(json.needsEmail),
      nextFromIso: typeof json.nextFromIso === 'string' ? json.nextFromIso : null,
    };
  } catch {
    return null;
  }
}

/**
 * M8 metric ladder (Phase 6) — fire-and-forget presence funnel event.
 * Pre-session, so it can't ride postSessionEvent: authenticated by
 * publicId + origin allowlist instead. PostHog counts only; a dropped
 * event is preferable to interrupting the visitor flow.
 */
export function postPresenceEvent(
  apiBaseUrl: string,
  body: {
    publicId: string;
    fingerprint: string;
    type:
      | 'presence-shown'
      | 'presence-accepted'
      | 'presence-dismissed'
      | 'offer-shown'
      | 'offer-accepted'
      | 'offer-declined'
      | 'offer-dismissed';
    surface?: 'bar' | 'launcher' | 'bubble' | 'engage' | 'card';
    act?: 'typed' | 'talked' | 'clicked';
    offerId?: string;
    presentation?: 'interstitial' | 'toast' | 'banner';
    device?: 'phone' | 'tablet' | 'desktop';
    deploymentId?: string;
    pageUrl: string;
  },
): void {
  try {
    void fetch(`${apiBaseUrl}/v1/presence/events`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => {});
  } catch {
    // Swallow — never break the visitor flow on tracking.
  }
}

// ============================================================
// Personalize v1 — POST /v1/decide
// ============================================================

export interface DecideRequest {
  publicId: string;
  fingerprint: string;
  pageUrl: string;
  context?: {
    /** Full utm_* set, prefix stripped (source/medium/campaign/term/content/…). */
    utm?: Record<string, string>;
    /** Scrubbed non-utm landing params incl. click IDs — see page-context.ts. */
    params?: Record<string, string>;
    referrer?: string;
    // Slice 6.6 — view drives which proactive tool the LLM picks
    // (propose_pilot_prompt vs update_engage_greeting).
    view?: string;
    // Opening Turn — boot page-view on a pre-conversation surface;
    // the server constrains the tool enum to the opening set.
    opening?: boolean;
    /** The visitor's moment this tick exists for (Phase C): dwell / scroll / exit. */
    moment?: { kind: 'dwell' | 'scroll' | 'exit'; detail?: string };
  };
}

/**
 * Slice 6 (LLM-as-policy) response shape. The proactive runtime emits
 * structured output: which tool to invoke + the per-tool args + a short
 * rationale. The widget dispatches the right side-effect based on
 * `toolCall.name`. `wait` and `suppress` never reach the widget (server
 * returns 204).
 */
export interface DecideResponse {
  decisionId: string;
  toolCall: {
    name:
      /** Action Links (D4): an arrival rule matched — start this Action, no LLM involved. */
      | 'open_action'
      | 'compose_opening'
      | 'propose_pilot_prompt'
      | 'update_engage_greeting'
      | 'open_content'
      | 'highlight_element'
      | 'send_email';
    args: Record<string, unknown>;
  };
  reasoning: string | null;
}

/**
 * Request a Personalize v1 decision for the current visitor + page.
 *
 * Returns null when:
 *   - Flag is off for the org (server replies 204)
 *   - No policy matched (server replies 204)
 *   - Network failure (we swallow — Personalize never breaks chat)
 *
 * Returns the rendered action otherwise. The caller decides what to
 * do with it based on `action.surface` (mount Pilot Prompt for
 * `prompt`, queue chat for `chat`, etc.).
 */
export async function postDecide(
  apiBaseUrl: string,
  req: DecideRequest,
  agentPreviewToken?: string | null,
): Promise<DecideResponse | null> {
  const preview = agentPreviewToken ? `?preview=${encodeURIComponent(agentPreviewToken)}` : '';
  try {
    const resp = await fetch(`${apiBaseUrl}/v1/decide${preview}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify(req),
    });
    if (resp.status === 204) return null;
    if (!resp.ok) return null;
    return (await resp.json()) as DecideResponse;
  } catch {
    return null;
  }
}

export async function createSession(
  apiBaseUrl: string,
  publicId: string,
  existingSessionId: string | null,
  fingerprint: string | null,
  signal?: AbortSignal,
  // Arrival context persisted onto the session row at create (P2.11) —
  // THIS call creates most sessions, so pageUrl/referrer/utm/launch
  // context must ride it or the row stays unattributed (the chat turn's
  // create-only persistence never fires for an existing session).
  arrival?: {
    context?: {
      pageUrl?: string;
      referrer?: string;
      utm?: Record<string, string>;
      params?: Record<string, string>;
    };
    launchContext?: Record<string, unknown>;
  },
): Promise<SessionResponse> {
  // Build the body conditionally so older servers (without fingerprint
  // support) tolerate the optional key, and so empty values don't go
  // over the wire as "" or null.
  const body: Record<string, unknown> = {};
  if (existingSessionId) body.sessionId = existingSessionId;
  if (fingerprint) body.fingerprint = fingerprint;
  if (arrival?.context) body.context = arrival.context;
  if (arrival?.launchContext && Object.keys(arrival.launchContext).length > 0) {
    body.launchContext = arrival.launchContext;
  }
  const res = await fetch(`${apiBaseUrl}/v1/agents/${publicId}/session`, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      accept: 'application/json',
    },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok) throw new Error(`session ${res.status}`);
  const json = (await res.json()) as SessionResponse;
  if (!json || typeof json.sessionId !== 'string') {
    throw new Error('session: malformed response');
  }
  return json;
}

/**
 * Action Links — start a declared Action on a LIVE session (the create
 * path carries it as `launchContext.action` instead). Resolves `accepted`;
 * a network failure is false too — the runner opens the panel plainly.
 */
export interface ActionFireResult {
  accepted: boolean;
  /** The declared target's kind ('conversation' when none); null when not accepted or on an older server. */
  targetKind: string | null;
}

export async function postActionFire(
  apiBaseUrl: string,
  input: { publicId: string; sessionId: string; fingerprint: string | null; action: string; source: 'page' | 'hash' | 'arrival' },
): Promise<ActionFireResult> {
  const { sessionId, ...body } = input;
  const res = await fetch(`${apiBaseUrl}/v1/sessions/${sessionId}/action`, {
    method: 'POST',
    headers: { 'content-type': 'application/json', accept: 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) return { accepted: false, targetKind: null };
  const json = (await res.json()) as { accepted?: unknown; targetKind?: unknown };
  const accepted = json?.accepted === true;
  return { accepted, targetKind: accepted && typeof json.targetKind === 'string' ? json.targetKind : null };
}

/** A 403 from /config whose code says the PLAN refused (not the origin). */
export function isPlanRefusal(err: unknown): boolean {
  const e = err as { status?: unknown; code?: unknown } | null;
  return e?.status === 403 && typeof e.code === 'string' && /^(plan|trial)-/.test(e.code);
}
